<svelte:options runes={false} />
<script>
  // Book weight-stream chart (TASK-20260728-researcher-observability-ui): stacked per-name weights at each
  // rebalance snapshot — how the book was steered over time. Top-5 names get the validated categorical hues
  // in fixed order (never cycled); the rest stack as the Other gray. A 2px surface gap separates stacked
  // segments (dataviz mark spec). The HTML legend below names every series (identity never color-alone).
  import { onMount } from 'svelte';
  import { COLORS } from './chartUtils.js';
  import { xForTime, timeDomain } from '../../../lib/lineage.js';
  import { BOOK_SERIES_COLORS } from '../../../lib/book.js';

  export let stream = { symbols: [], points: [] }; // weightStream() output
  export let capWeight = null; // per-name cap (e.g. 0.10) — drawn as a reference for the top series

  let canvas;
  let ctx;
  let container;
  let width = 700;
  const height = 240;
  const PAD = { top: 14, right: 56, bottom: 24, left: 10 };
  let hoverIdx = -1;

  $: points = stream?.points ?? [];
  $: symbols = stream?.symbols ?? [];
  $: hasData = points.length > 0;
  $: domain = timeDomain([points]);
  $: maxGross = Math.max(0.01, ...points.map((p) => symbols.reduce((s, sym) => s + (p.weights[sym] ?? 0), 0) + (p.other ?? 0)));

  function colorOf(i) { return BOOK_SERIES_COLORS.series[i] ?? BOOK_SERIES_COLORS.other; }

  function draw() {
    if (!ctx || !hasData) return;
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, width, height);

    const plotW = width - PAD.left - PAD.right;
    const plotH = height - PAD.top - PAD.bottom;
    const yOf = (v) => PAD.top + (1 - v / (maxGross * 1.08)) * plotH;
    const xOf = (t) => xForTime(Date.parse(t), domain, PAD.left, plotW);

    // Grid + right labels (gross weight).
    ctx.font = '10px sans-serif';
    for (let i = 0; i <= 4; i++) {
      const v = (maxGross * 1.08 * (4 - i)) / 4;
      const y = yOf(v);
      ctx.strokeStyle = COLORS.grid;
      ctx.beginPath(); ctx.moveTo(PAD.left, y); ctx.lineTo(width - PAD.right, y); ctx.stroke();
      ctx.fillStyle = COLORS.label;
      ctx.textAlign = 'right';
      ctx.fillText(`${(v * 100).toFixed(0)}%`, width - 5, y + 3);
    }

    // Stacked columns per snapshot (columns, not areas: rebalance points are discrete events, and a column
    // reads honestly when snapshots are irregular). 2px gaps between stacked segments.
    const n = points.length;
    const cw = Math.max(3, Math.min(22, (plotW / Math.max(1, n)) * 0.7));
    points.forEach((p, pi) => {
      const x = xOf(p.time);
      let acc = 0;
      const segs = [...symbols.map((sym, si) => ({ v: p.weights[sym] ?? 0, color: colorOf(si) })),
        { v: p.other ?? 0, color: BOOK_SERIES_COLORS.other }];
      for (const seg of segs) {
        if (seg.v <= 0) continue;
        const y0 = yOf(acc);
        const y1 = yOf(acc + seg.v);
        ctx.fillStyle = seg.color;
        ctx.globalAlpha = pi === hoverIdx ? 1 : 0.88;
        ctx.fillRect(x - cw / 2, y1, cw, Math.max(1, y0 - y1 - 2)); // 2px stacked-segment gap
        acc += seg.v;
      }
      ctx.globalAlpha = 1;
    });

    // Per-name cap reference (spec §8 max weight) — meaningful against a single segment, drawn dashed.
    if (capWeight != null && capWeight > 0 && capWeight < maxGross * 1.08) {
      const y = yOf(capWeight);
      ctx.strokeStyle = '#d8a000';
      ctx.setLineDash([5, 4]);
      ctx.beginPath(); ctx.moveTo(PAD.left, y); ctx.lineTo(width - PAD.right, y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#d8a000';
      ctx.textAlign = 'left';
      ctx.fillText(`per-name cap ${(capWeight * 100).toFixed(0)}%`, PAD.left + 4, y - 4);
    }

    // Time labels.
    const span = domain.max - domain.min;
    if (span > 0) {
      ctx.fillStyle = COLORS.label;
      ctx.textAlign = 'center';
      for (let i = 0; i <= 5; i++) {
        const t = domain.min + (span * i) / 5;
        ctx.fillText(new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          PAD.left + (plotW * i) / 5, height - 6);
      }
    }
  }

  function onMove(e) {
    if (!hasData) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    let best = -1;
    let bestDx = 14;
    points.forEach((p, i) => {
      const dx = Math.abs(xForTime(Date.parse(p.time), domain, PAD.left, width - PAD.left - PAD.right) - mx);
      if (dx < bestDx) { bestDx = dx; best = i; }
    });
    hoverIdx = best;
  }
  function onLeave() { hoverIdx = -1; }

  $: hovered = hoverIdx >= 0 ? points[hoverIdx] : null;
  $: hoveredGross = hovered ? (symbols.reduce((s, sym) => s + (hovered.weights[sym] ?? 0), 0) + (hovered.other ?? 0)) : 0;
  $: if (ctx && canvas && hasData && width && (hoverIdx || true) && stream && capWeight !== undefined) draw();
  $: if (canvas && !ctx) { ctx = canvas.getContext('2d'); resize(); }

  function resize() {
    if (!container) return;
    width = Math.max(300, container.offsetWidth);
  }

  onMount(() => {
    if (canvas) ctx = canvas.getContext('2d');
    resize();
    draw();
    const onResize = () => { resize(); draw(); };
    window.addEventListener('resize', onResize);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
    if (ro && container) ro.observe(container);
    return () => { window.removeEventListener('resize', onResize); ro?.disconnect(); };
  });
</script>

<div class="bw-root" bind:this={container}>
  <h4 class="bw-title">Book weights per rebalance</h4>
  {#if hasData}
    <canvas bind:this={canvas} width={width} height={height} tabindex="0"
      aria-label={`Stacked book weights across ${points.length} rebalance snapshots; top names ${symbols.join(', ') || 'none'}.`}
      on:mousemove={onMove} on:mouseleave={onLeave}></canvas>
    <div class="bw-legend">
      {#each symbols as sym, i (sym)}
        <span class="bw-item"><span class="bw-swatch" style={`background:${BOOK_SERIES_COLORS.series[i]}`}></span>{sym.replace('_', '-')}</span>
      {/each}
      <span class="bw-item"><span class="bw-swatch" style={`background:${BOOK_SERIES_COLORS.other}`}></span>Other</span>
    </div>
    <div class="bw-hover" aria-live="polite">
      {#if hovered}
        {new Date(hovered.time).toLocaleString()} · gross {(hoveredGross * 100).toFixed(1)}%
        {#each symbols as sym}{#if hovered.weights[sym] != null} · {sym.replace('_', '-')} {(hovered.weights[sym] * 100).toFixed(1)}%{/if}{/each}
        {#if hovered.other > 0} · other {(hovered.other * 100).toFixed(1)}%{/if}
      {:else}&nbsp;{/if}
    </div>
  {:else}
    <div class="empty-state"><p class="empty-state-description">No book snapshots in this window yet.</p></div>
  {/if}
</div>

<style>
  .bw-root { width: 100%; }
  .bw-title { margin: 0 0 0.4rem; font-size: 0.8rem; color: var(--text-secondary, #b5b6b8); }
  canvas { display: block; width: 100%; border: 1px solid var(--border-subtle, #1e1f22); border-radius: 6px; }
  canvas:focus-visible { outline: 2px solid var(--accent, #3c78f0); outline-offset: 1px; }
  .bw-legend { display: flex; flex-wrap: wrap; gap: 0.8rem; padding: 0.35rem 0; font-size: 0.72rem; color: var(--text-muted, #8a8b8d); }
  .bw-item { display: inline-flex; align-items: center; gap: 0.3rem; }
  .bw-swatch { width: 10px; height: 10px; border-radius: 2px; display: inline-block; }
  .bw-hover { min-height: 1.1rem; font-size: 0.75rem; color: var(--text-secondary, #b5b6b8); font-variant-numeric: tabular-nums; }
</style>
